import { useState, createContext, useContext, useMemo, useEffect } from "react";
import { useSnackbar } from "notistack";
import { useParams } from "react-router-dom";
import {
    Box,
    IconButton,
    CircularProgress,
    TextField,
    Grid,
    Typography,
    List,
    ListItem,
    ListItemText,
    Paper,
    Divider,
    Fab,
    LinearProgress,
    Dialog,
    Chip,
    DialogTitle,
    DialogContent,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    DialogActions,
    InputLabel,
    Select,
    MenuItem,
} from "@mui/material";
import { red, blue, green, orange } from "@mui/material/colors";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import Layout from "../components/Layout/Layout";
import ErrorPage from "./ErrorPage";
import {
    useFetchPickingLineByIdQuery as useFetchPickingLine,
    useFetchfetchPickingLineNewProductsQuery as useFetchNewProducts,
    useFetchfetchPickingLineExtraBoxContentQuery as useFetchExtraBoxContents,
    useUpdatePickingLineMutation as useUpdatePickingLine,
    useCreatePickingLineProductMutation as useCreatePickingLineProduct,
    useFetchStockTallyQuery as useFetchStockTally,
    useCreateStockActivityMutation as useCreateStockActivity,
} from "../api";
import { PICKING_LINE_STATUS, PICKING_LINE_STATUS_LABEL } from "../constants";
import LoadingButton from "../components/LoadingButton/LoadingButton";

const PickingLineContext = createContext(null);

const PickingLineDetails = () => {
    const { id: pickingLineId } = useParams();
    const { data: pickingLine, isLoading, isError } = useFetchPickingLine(pickingLineId);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [stockDialogOpen, setStockDialogOpen] = useState(false);

    if (isError) return <ErrorPage />;
    if (isLoading) return <LoadingPage />;

    return (
        <PickingLineContext.Provider value={pickingLine}>
            <Layout color="rgb(249 98 125)" title={<Header />}>
                <Content setDialogOpen={setDialogOpen} setStockDialogOpen={setStockDialogOpen} />
            </Layout>
            <NewProductsDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onSubmit={() => setDialogOpen(false)} />
            <StockTallyDialog open={stockDialogOpen} onClose={() => setStockDialogOpen(false)} />
        </PickingLineContext.Provider>
    );
};

const LoadingPage = () => (
    <Layout color="rgb(249 98 125)" title="Warehouse">
        <Box display="flex" justifyContent="center">
            <CircularProgress />
        </Box>
    </Layout>
);

const Header = () => {
    const pickingLine = useContext(PickingLineContext);

    return (
        <Box>
            <Typography variant="h5">{pickingLine?.project?.name || pickingLine?.name || "Project Missing"}</Typography>
            <Typography variant="h6">{pickingLine?.employee?.name || "Employee unassigned"}</Typography>
        </Box>
    );
};

const Content = ({ setDialogOpen, setStockDialogOpen }) => (
    <Grid container direction="column" spacing={2}>
        <Grid item>
            <PickingLineInfo onOpenStockTally={() => setStockDialogOpen(true)} />
        </Grid>
        <Grid item>
            <NewProductsList onClickAddProduct={() => setDialogOpen(true)} />
        </Grid>
        <Grid item>
            <ExtraBoxProductsList />
        </Grid>
        <Grid item>
            <ProjectDescription />
        </Grid>
    </Grid>
);

const PickingLineInfo = ({ onOpenStockTally }) => {
    const pickingLine = useContext(PickingLineContext);
    const { name: boxLocationName } = pickingLine.boxLocation || "";

    const { enqueueSnackbar } = useSnackbar();
    const { mutate, isLoading } = useUpdatePickingLine();

    // Local state
    const INITIAL_STATUS = pickingLine.status;
    const INITIAL_NUM_BOXES = pickingLine.numberOfBoxes;
    const INITIAL_NUM_EXTRA_BOXES = pickingLine.numberOfExtraBoxes;
    const INITIAL_BOX_LOCATION = boxLocationName;

    const [status, setStatus] = useState(INITIAL_STATUS);
    const [numberOfBoxes, setNumberOfBoxes] = useState(INITIAL_NUM_BOXES);
    const [numberOfExtraBoxes, setNumberOfExtraBoxes] = useState(INITIAL_NUM_EXTRA_BOXES);
    const [boxLocation, setBoxLocation] = useState(INITIAL_BOX_LOCATION);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    const isBoxesInfoValid = numberOfBoxes !== "" && numberOfBoxes > 0 && numberOfExtraBoxes !== "" && numberOfExtraBoxes > 0 && boxLocation !== "";

    useEffect(() => {
        setHasUnsavedChanges(status !== INITIAL_STATUS || numberOfBoxes !== INITIAL_NUM_BOXES || numberOfExtraBoxes !== INITIAL_NUM_EXTRA_BOXES || boxLocation !== INITIAL_BOX_LOCATION);
    }, [status, numberOfBoxes, numberOfExtraBoxes, boxLocation, INITIAL_BOX_LOCATION, INITIAL_STATUS, INITIAL_NUM_BOXES, INITIAL_NUM_EXTRA_BOXES]);

    // Handlers
    const handleSave = () => {
        if (!hasUnsavedChanges || !isBoxesInfoValid) {
            return;
        }

        mutate(
            {
                pickingLineId: pickingLine.id,
                numberOfBoxes: numberOfBoxes,
                numberOfExtraBoxes: numberOfExtraBoxes,
                status: status,
                boxLocationName: boxLocation,
            },
            {
                onSuccess: () => {
                    enqueueSnackbar("Updated successfully", {
                        variant: "success",
                        anchorOrigin: { vertical: "bottom", horizontal: "center" },
                        autoHideDuration: 3000,
                    });
                },
                onError: () => {
                    enqueueSnackbar("Error occurred while updating", {
                        variant: "error",
                        anchorOrigin: { vertical: "bottom", horizontal: "center" },
                        autoHideDuration: 3000,
                    });
                },
            }
        );
    };

    const handleChangeStatus = (event) => setStatus(event.target.value);
    const handleChangeNumberOfBoxes = (event) => {
        const value = event.target.value === "" ? "" : event.target.valueAsNumber;
        if (value === "+" || value === "-") return;
        setNumberOfBoxes(value === 0 ? "" : value);
    };
    const handleChangeNumberOfExtraBoxes = (event) => {
        const value = event.target.value === "" ? "" : event.target.valueAsNumber;
        if (value === "+" || value === "-") return;
        setNumberOfExtraBoxes(value < 0 ? 0 : value);
    };
    const handleChangeBoxLocation = (event) => {
        const value = event.target.value;
        setBoxLocation(value);
    };

    const handleBlurNumberOfBoxes = () => {
        if (numberOfBoxes === "") setNumberOfBoxes(INITIAL_NUM_BOXES);
    };
    const handleBlurNumberOfExtraBoxes = () => {
        if (numberOfExtraBoxes === "") setNumberOfExtraBoxes(INITIAL_NUM_EXTRA_BOXES);
    };
    const handleBlurBoxLocation = () => {
        if (boxLocation === "") setBoxLocation(INITIAL_BOX_LOCATION);
    };
    const handleCancel = () => {
        setStatus(pickingLine.status);
        setNumberOfBoxes(INITIAL_NUM_BOXES);
        setNumberOfExtraBoxes(INITIAL_NUM_EXTRA_BOXES);
        setBoxLocation(INITIAL_BOX_LOCATION);
    };

    return (
        <Box sx={{ gap: "1rem", display: "flex", flexDirection: "column" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <Select
                        labelId="status-label"
                        fullWidth
                        value={status}
                        onChange={handleChangeStatus}
                        renderValue={(value) => {
                            const label = PICKING_LINE_STATUS_LABEL[value];
                            const backgroundColor =
                                value === PICKING_LINE_STATUS.I_HOLFI
                                    ? orange[500]
                                    : value === PICKING_LINE_STATUS.I_YFIRFERD
                                    ? red[500]
                                    : value === PICKING_LINE_STATUS.KOMID_I_HILLU
                                    ? green[500]
                                    : blue[500];
                            return (
                                <Box>
                                    <Chip label={label} sx={{ backgroundColor: backgroundColor, fontSize: "1rem", color: "#fff" }} />
                                </Box>
                            );
                        }}
                    >
                        {Object.values(PICKING_LINE_STATUS).map((statusCode) => {
                            const label = PICKING_LINE_STATUS_LABEL[statusCode];
                            return (
                                <MenuItem key={`${label}-${statusCode}`} value={statusCode}>
                                    {label}
                                </MenuItem>
                            );
                        })}
                    </Select>
                </Box>
                <StockTallyButton onClick={onOpenStockTally} />
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography sx={{ fontWeight: "bold" }}>Number of Boxes</Typography>
                <TextField
                    value={numberOfBoxes}
                    onChange={handleChangeNumberOfBoxes}
                    onBlur={handleBlurNumberOfBoxes}
                    error={!numberOfBoxes}
                    type="number"
                    variant="outlined"
                    size="small"
                    sx={{ width: 60, backgroundColor: "#fff" }}
                />
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography sx={{ fontWeight: "bold" }}>Number of Extra Boxes</Typography>
                <TextField
                    value={numberOfExtraBoxes}
                    onChange={handleChangeNumberOfExtraBoxes}
                    onBlur={handleBlurNumberOfExtraBoxes}
                    error={!numberOfExtraBoxes}
                    type="number"
                    variant="outlined"
                    size="small"
                    sx={{ width: 60, backgroundColor: "#fff" }}
                />
            </Box>

            <Box sx={{ gap: "1rem", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography sx={{ fontWeight: "bold" }}>Location</Typography>
                <TextField
                    value={boxLocation}
                    onChange={handleChangeBoxLocation}
                    onBlur={handleBlurBoxLocation}
                    error={!boxLocation}
                    variant="outlined"
                    size="small"
                    sx={{ backgroundColor: "#fff" }}
                />
            </Box>

            <Grid item container spacing={1}>
                <Grid item>
                    <LoadingButton
                        onClick={handleSave}
                        loading={isLoading}
                        variant="outlined"
                        color="primary"
                        sx={{ borderRadius: 50 }}
                        disabled={!hasUnsavedChanges || isLoading || !isBoxesInfoValid}
                    >
                        Save
                    </LoadingButton>
                </Grid>
                <Grid item>
                    <Button onClick={handleCancel} variant="outlined" color="secondary" sx={{ borderRadius: 50 }} disabled={!hasUnsavedChanges || isLoading}>
                        Cancel
                    </Button>
                </Grid>
            </Grid>
            <Divider />
        </Box>
    );
};

const NewProductsList = ({ onClickAddProduct }) => {
    const pickingLine = useContext(PickingLineContext);
    const { data: newProducts, isLoading, isError } = useFetchNewProducts(pickingLine?.id, { enabled: !!pickingLine?.id });

    return (
        <Box component={Paper} variant="outlined" elevation={0} p={2}>
            <Box display="flex" alignItems="center" justifyContent="space-between" pb={1}>
                <Typography style={{ fontSize: "1.1rem" }}>New Products</Typography>
                <Fab color="secondary" size="small" onClick={onClickAddProduct} disabled={pickingLine?.status !== PICKING_LINE_STATUS.I_YFIRFERD}>
                    <AddIcon />
                </Fab>
            </Box>
            <Divider style={{ backgroundColor: "darkgray" }} />

            {isLoading && <LinearProgress />}
            {isError && <Typography color="textSecondary">Error fetching products</Typography>}

            <Box style={{ maxHeight: 200, overflow: "scroll" }}>
                <List style={{ height: 200 }}>
                    {newProducts &&
                        newProducts.map((item) => (
                            <ListItem key={item.id} disableGutters style={{ textDecoration: item?.depricated === true ? "line-through" : "none" }}>
                                <Box display="flex" width="100%" justifyContent="space-around">
                                    <Box width="80%">
                                        <ListItemText primary={item.product.name} secondary={item.product.productNumber} style={{ margin: 0 }} />
                                    </Box>
                                    <Box>
                                        <Typography color="textSecondary">x {item.quantity}</Typography>
                                    </Box>
                                </Box>
                            </ListItem>
                        ))}
                </List>
            </Box>
        </Box>
    );
};

const ExtraBoxProductsList = () => {
    const pickingLine = useContext(PickingLineContext);
    const { data: extraBoxContents, isLoading, isError } = useFetchExtraBoxContents(pickingLine?.id, { enabled: !!pickingLine?.id });

    return (
        <Box component={Paper} variant="outlined" elevation={0} p={2}>
            <Box>
                <Typography style={{ fontSize: "1.1rem" }}>Extra</Typography>
            </Box>
            <Box my={1}>
                <Divider style={{ backgroundColor: "darkgray" }} />
            </Box>

            {isLoading && <LinearProgress />}
            {isError && <Typography color="textSecondary">Error fetching extra box products</Typography>}

            <Box style={{ maxHeight: 200, overflow: "scroll" }}>
                <List style={{ height: 200 }}>
                    {extraBoxContents &&
                        extraBoxContents.map((item) => (
                            <ListItem key={item.id} disableGutters>
                                <Box display="flex" width="100%" justifyContent="space-around">
                                    <Box width="80%">
                                        <ListItemText primary={item.product.name} secondary={item.product.productNumber} style={{ margin: 0 }} />
                                    </Box>
                                    <Box>
                                        <Typography color="textSecondary">x {item.quantity}</Typography>
                                    </Box>
                                </Box>
                            </ListItem>
                        ))}
                </List>
            </Box>
        </Box>
    );
};

const ProjectDescription = () => {
    const pickingLine = useContext(PickingLineContext);
    const description = pickingLine?.project?.description;

    return (
        <Box component={Paper} variant="outlined" elevation={0} p={2}>
            <Box>
                <Typography style={{ fontSize: "1.1rem" }}>Description</Typography>
            </Box>
            <Box my={1}>
                <Divider style={{ backgroundColor: "darkgray" }} />
            </Box>

            <Typography color={description ? "textPrimary" : "textSecondary"} style={{ whiteSpace: "pre-wrap" }}>
                {description || "No description"}
            </Typography>
        </Box>
    );
};

const StockTallyButton = ({ onClick }) => {
    const pickingLine = useContext(PickingLineContext);
    const addressLocationId = pickingLine?.project?.address?.id;

    return (
        <Button
            variant="outlined"
            onClick={onClick}
            disabled={!addressLocationId}
            sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
        >
            Stock
        </Button>
    );
};

const StockTallyDialog = ({ open, onClose }) => {
    const pickingLine = useContext(PickingLineContext);
    const { enqueueSnackbar } = useSnackbar();
    const addressLocationId = pickingLine?.project?.address?.id;
    const addressName = pickingLine?.project?.address?.name;

    const [selectedId, setSelectedId] = useState(null);
    const [alteration, setAlteration] = useState("");

    const { data: stockTally, isLoading, isError } = useFetchStockTally(addressLocationId, { enabled: open && !!addressLocationId });

    const clearSelection = () => {
        setSelectedId(null);
        setAlteration("");
    };

    const { mutate: createStockActivity, isLoading: isSaving } = useCreateStockActivity({
        onSuccess: () => {
            // The tally itself is recalculated by an asynchronous CRM plugin, so the
            // quantity on screen will not move until that has run.
            enqueueSnackbar("Alteration recorded", { variant: "success" });
            clearSelection();
        },
        onError: () => enqueueSnackbar("Could not record the alteration", { variant: "error" }),
    });

    const handleClose = () => {
        clearSelection();
        onClose();
    };

    const handleSave = (row) => {
        const quantity = parseFloat(alteration);
        if (!quantity) return;

        createStockActivity({
            addressLocationId,
            stockTallyId: row.id,
            productId: row.product.id,
            quantity,
            name: `${addressName} - ${row.product.name} - Afstemming`,
        });
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{ sx: { height: "90vh", maxHeight: "90vh" } }}
        >
            <DialogTitle sx={{ py: 1.5 }}>
                {addressName || "Stock"}
            </DialogTitle>
            <DialogContent dividers>
                {isLoading && <LinearProgress />}
                {isError && <Typography color="textSecondary">Error fetching stock</Typography>}
                {stockTally?.length === 0 && <Typography color="textSecondary">No stock registered for this address</Typography>}

                <List dense disablePadding>
                    {stockTally?.map((row) => (
                        <ListItem key={row.id} disableGutters divider sx={{ display: "block", py: 0.5 }}>
                            <Box display="flex" width="100%" alignItems="center" gap={1}>
                                <ListItemText
                                    primary={row.product?.name || "Unknown product"}
                                    secondary={row.product?.productNumber}
                                    primaryTypographyProps={{ variant: "body2" }}
                                    secondaryTypographyProps={{ variant: "caption", noWrap: true }}
                                    sx={{ m: 0, minWidth: 0 }}
                                />
                                <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: "nowrap" }}>
                                    x {row.quantity}
                                </Typography>
                                <IconButton
                                    size="small"
                                    edge="end"
                                    aria-label={`Alter ${row.product?.name || "product"}`}
                                    disabled={!row.product?.id}
                                    onClick={() => (selectedId === row.id ? clearSelection() : setSelectedId(row.id))}
                                >
                                    <EditIcon fontSize="small" />
                                </IconButton>
                            </Box>

                            {selectedId === row.id && (
                                <Box display="flex" alignItems="center" gap={1} pb={1}>
                                    <TextField
                                        autoFocus
                                        size="small"
                                        type="number"
                                        placeholder="+/-"
                                        value={alteration}
                                        onChange={(event) => setAlteration(event.target.value)}
                                        disabled={isSaving}
                                        sx={{ width: 90 }}
                                    />
                                    <LoadingButton size="small" loading={isSaving} disabled={!parseFloat(alteration)} onClick={() => handleSave(row)}>
                                        Save
                                    </LoadingButton>
                                    <Button size="small" disabled={isSaving} onClick={clearSelection}>
                                        Cancel
                                    </Button>
                                </Box>
                            )}
                        </ListItem>
                    ))}
                </List>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

const NewProductsDialog = ({ open, onClose }) => {
    const pickingLine = useContext(PickingLineContext);
    const { enqueueSnackbar } = useSnackbar();

    const { data: extraBoxContents } = useFetchExtraBoxContents(pickingLine?.id, { enabled: !!pickingLine?.id });
    const { data: newProducts } = useFetchNewProducts(pickingLine?.id, { enabled: !!pickingLine?.id });

    const { mutate, isLoading } = useCreatePickingLineProduct();

    // Local state
    const INITIAL_PRODUCT_ID = "";
    const INITIAL_QUANTITY = 1;
    const [selectedProduct, setSelectedProduct] = useState(INITIAL_PRODUCT_ID);
    const [productQuantity, setProductQuantity] = useState(INITIAL_QUANTITY);
    const [isWrittenOff, setIsWrittenOff] = useState(false);
    const [employeeComment, setEmployeeComment] = useState("");

    // Prepare products list
    const products = useMemo(() => {
        const items = [...(newProducts ? newProducts : []), ...(extraBoxContents ? extraBoxContents : [])];
        return Array.from(
            items
                .reduce((map, item) => {
                    if (!map.has(item.product.id)) {
                        map.set(item.product.id, item.product);
                    }
                    return map;
                }, new Map())
                .values()
        );
    }, [newProducts, extraBoxContents]);

    // Handlers
    const handleCancel = () => {
        setSelectedProduct(INITIAL_PRODUCT_ID);
        setProductQuantity(INITIAL_QUANTITY);
        setIsWrittenOff(false);
        setEmployeeComment("");
        onClose();
    };

    const handleSetProductQuantity = (event) => {
        const quantity = parseFloat(event.target.value);
        if (quantity < 0) return;
        setProductQuantity(quantity);
    };

    const isValid = selectedProduct !== INITIAL_PRODUCT_ID && productQuantity > 0;

    const handleSave = () => {
        if (!isValid) return;

        mutate(
            {
                projectId: pickingLine.project.id,
                productId: selectedProduct,
                quantity: productQuantity,
                writtenOff: isWrittenOff,
                comment: employeeComment,
            },
            {
                onSuccess: () => {
                    enqueueSnackbar("Product entry added successfully", {
                        variant: "success",
                        anchorOrigin: { vertical: "bottom", horizontal: "center" },
                        autoHideDuration: 3000,
                    });
                    onClose();
                },
                onError: () => {
                    enqueueSnackbar("Error occurred while adding product entry", {
                        variant: "error",
                        anchorOrigin: { vertical: "bottom", horizontal: "center" },
                        autoHideDuration: 3000,
                    });
                },
            }
        );
    };

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>Add/Remove Product</DialogTitle>
            <DialogContent>
                <FormControl fullWidth variant="filled" margin="normal">
                    <InputLabel htmlFor="product-select">Product</InputLabel>
                    <Select
                        label="Product"
                        value={selectedProduct}
                        onChange={(e) => setSelectedProduct(e.target.value)}
                        inputProps={{
                            name: "product",
                            id: "product-select",
                        }}
                    >
                        {products.map((product) => (
                            <MenuItem key={product.id} value={product.id}>
                                {product.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <TextField
                    value={productQuantity}
                    onChange={handleSetProductQuantity}
                    error={!productQuantity}
                    label="Quantity"
                    type="number"
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    inputProps={{ step: "0.01" }}
                />
                <FormControlLabel label="Written off" control={<Checkbox checked={isWrittenOff} onChange={(e) => setIsWrittenOff(e.target.checked)} color="primary" />} />
                <TextField fullWidth label="Comment" value={employeeComment} onChange={(e) => setEmployeeComment(e.target.value)} variant="outlined" margin="normal" multiline minRows={2} />
            </DialogContent>
            <DialogActions>
                <Box display="flex" py={1}>
                    <Box>
                        <LoadingButton variant="outlined" color="secondary" size="small" loading={isLoading} disabled={isLoading} onClick={handleCancel}>
                            Cancel
                        </LoadingButton>
                    </Box>
                    <Box ml={1}>
                        <LoadingButton variant="outlined" color="primary" size="small" disabled={isLoading || !isValid} onClick={handleSave}>
                            Save
                        </LoadingButton>
                    </Box>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default PickingLineDetails;
